import React, { useContext, useEffect, useState } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Form, Button, Spinner } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";
import SchoolEnrollmentTable from "../../../components/tables/SchoolEnrollmentTable";

export default function SchoolEnrollments() {
  const { t } = useContext(TranslatorContext);

  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [levelFilter, setLevelFilter] = useState("");

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    from: 0,
    to: 0,
  });
  const [filterOptions, setFilterOptions] = useState({
    schools: [],
    levels: [],
    statuses: [],
  });

  const AuthToken = localStorage.getItem("AuthUser");

  axios.defaults.headers.common[
    "Authorization"
  ] = `Bearer ${AuthToken}`;

  // wait for the user to stop typing before hitting the API
  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 500);

    return () => clearTimeout(delay);
  }, [search]);

  useEffect(() => {
    let ignore = false;

    const params = { page };
    if (debouncedSearch) params.search = debouncedSearch;
    if (statusFilter) params.status = statusFilter;
    if (schoolFilter) params.school_id = schoolFilter;
    if (levelFilter) params.level = levelFilter;

    setLoading(true);

    axios
      .get(process.env.REACT_APP_API_URL + "/school-enrollments", { params })
      .then((res) => {
        if (ignore) return;

        const mappedData = res.data.enrollments.map((item) => ({
          id: item.id,

          going_image:
            process.env.REACT_APP_IMG_URL +  item.going_image,

          f_number: item.student?.family?.f_number,

          student_name: item.student?.name,

          dob: item.student?.dob,

          gender: item.student?.gender_id === 1 ? "Male" : "Female",

          guardian_name: item.student?.family?.beneficiary_name,

          cnic: item.student?.cnic,

          religion: item.student?.family?.faith?.name ?? "-",

          address: item.student?.family?.present_address,

          contact: item.student?.family?.mob,

          student_image:
            process.env.REACT_APP_IMG_URL + item.student_image,

          school_category: item.school?.authority,

          school_name: item.school?.name,

          level: item.school?.level,

          class_name: item.school_class?.name,

          enrollment_date: item.admission_date,

          fee: item.fee,

          cost: item.cost,

          total: Number(item.fee) + Number(item.cost),

          invoice:
            process.env.REACT_APP_IMG_URL  + item.fee_receipt,

          status: item.status,

          action: {
            edit: "edit",
            delete: "delete",
          },
        }));

        setTrainings(mappedData);
        // fall back to a single page if the API has not been updated yet
        setPagination(
          res.data.pagination || {
            current_page: 1,
            last_page: 1,
            total: mappedData.length,
            from: mappedData.length ? 1 : 0,
            to: mappedData.length,
          }
        );
        if (res.data.filters) setFilterOptions(res.data.filters);
      })
      .catch((error) => {
        if (!ignore) console.error(error);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [page, debouncedSearch, statusFilter, schoolFilter, levelFilter]);

  // also show the loader while the search box is waiting for its debounce
  const busy = loading || search.trim() !== debouncedSearch;

  const changeFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  const resetFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setStatusFilter("");
    setSchoolFilter("");
    setLevelFilter("");
    setPage(1);
  };

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">
                School Enrollments ({pagination.total})
              </h3>
              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item">
                  <Link
                    to="#"
                    className="mc-breadcrumb-link"
                  >
                    {t("home")}
                  </Link>
                </li>

                <li className="mc-breadcrumb-item">
                  <Link
                    to="#"
                    className="mc-breadcrumb-link"
                  >
                    {t(
                      "School Enrollments"
                    )}
                  </Link>
                </li>

                <li className="mc-breadcrumb-item">
                  List
                </li>
              </ul>
            </div>
          </div>
        </Col>

        <Col xl={12}>
          <div className="mc-card">


            {/* Filters */}
            <Row className="mb-4">
              <Col md={3}>
                <Form.Control
                  type="text"
                  placeholder="Search FSF-ID / Name / CNIC / School"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Col>

              <Col md={2}>
                <Form.Select
                  value={statusFilter}
                  onChange={changeFilter(setStatusFilter)}
                >
                  <option value="">All Status</option>

                  {filterOptions.statuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={3}>
                <Form.Select
                  value={schoolFilter}
                  onChange={changeFilter(setSchoolFilter)}
                >
                  <option value="">All Schools</option>

                  {filterOptions.schools.map((school) => (
                    <option key={school.id} value={school.id}>
                      {school.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={3}>
                <Form.Select
                  value={levelFilter}
                  onChange={changeFilter(setLevelFilter)}
                >
                  <option value="">All Levels</option>

                  {filterOptions.levels.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={1}>
                <Button
                  variant="secondary"
                  className="w-100"
                  onClick={resetFilters}
                >
                  Reset
                </Button>
              </Col>
            </Row>

            <Row>
              <Col xl={12}>
                {busy && (
                  <h5 className="text-center my-3">
                    <Spinner animation="border" variant="success" /> Loading Data...
                  </h5>
                )}
                {!busy && trainings.length === 0 && (
                  <h5 className="text-center my-3">No enrollments found.</h5>
                )}
                <div
                  style={{
                    opacity: busy ? 0.4 : 1,
                    pointerEvents: busy ? "none" : "auto",
                  }}
                >
                <SchoolEnrollmentTable
                  thead={[
                    "Sr. No",
                    "Picture of Student Going to School / Institute",
                    "FSF-ID",
                    "Student Name",
                    "Date of Birth",
                    "Gender (M/F)",
                    "Father/Guardian Name",
                    "CNIC",
                    "Religion",
                    "Present Address",
                    "Contact",
                    "Student Picture at Enrollment",
                    "School / Institute Category (Government/Private)",
                    "Name of School / Institute",
                    "Level (Primary/Elementary/Secondary/College/University)",
                    "Class / Course Enrollment",
                    "Enrollment Date",
                    "Legal Charges and Fee",
                    "Material Cost",
                    "Total Amount (PKR)",
                    "Invoice Image",

                    // "Actions"
                  ]}
                  tbody={trainings}
                  startIndex={pagination.from || 1}
                />
                </div>
              </Col>
            </Row>

            <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
              <Button
                style={{ backgroundColor: "#077834" }}
                disabled={busy || pagination.current_page <= 1}
                onClick={() => setPage(pagination.current_page - 1)}
              >
                Prev
              </Button>
              <span>
                Page {pagination.current_page} of {pagination.last_page}
                {pagination.total > 0 &&
                  ` (${pagination.from}-${pagination.to} of ${pagination.total})`}
              </span>
              <Button
                style={{ backgroundColor: "#077834" }}
                disabled={busy || pagination.current_page >= pagination.last_page}
                onClick={() => setPage(pagination.current_page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </PageLayout>
  );
}
